import React from 'react'

import {
  Box,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'

import { getPlatformName } from '$extensionSrc/data/platform/name'
import { PLATFORM } from '$extensionSrc/utils/enums'
import { getLogoFromPlatform } from '$extensionSrc/utils/platformMetadataUtils'

import styles from './UsageTable.module.scss'
import { useSorting } from '../../../utils/useSorting'

// Platform icon component using the existing utility
function PlatformIcon({ platform }: { platform: string }) {
  const platformEnum = PLATFORM[platform as keyof typeof PLATFORM]
  if (!platformEnum) {
    return <span className={styles.platformIcon}>🔗</span>
  }
  return getLogoFromPlatform(platformEnum, styles.platformIcon)
}

function PlatformName({ platform }: { platform: string }) {
  const platformEnum = PLATFORM[platform as keyof typeof PLATFORM]
  if (!platformEnum) {
    return platform
  }
  return getPlatformName(platformEnum)
}

type PlatformDataItem = {
  platform: string
  [key: string]: any
}

interface UsageTableProps {
  loading?: boolean
  platformData?: Array<PlatformDataItem | null>
  columns: Array<{
    key: string
    label: string
    align?: 'left' | 'right' | 'center'
    format?: (value: any) => string
  }>
}

export default function UsageTable({
  loading = false,
  platformData = [],
  columns,
}: UsageTableProps) {
  // Filter out null values and use the sorting hook
  const filteredData = platformData.filter(
    (item): item is PlatformDataItem => item !== null,
  )

  const { sortedData, handleSort, getSortIndicator } = useSorting({
    data: filteredData,
    defaultSortKey: (columns[0]?.key || 'platform') as keyof PlatformDataItem,
    defaultDirection: 'desc',
  })

  return (
    <Box className={styles.container}>
      <Table size="small" className={styles.table}>
        <TableHead>
          <TableRow>
            <TableCell onClick={() => handleSort('platform')}>
              Platforms{getSortIndicator('platform')}
            </TableCell>
            {columns.map((col) => (
              <TableCell
                key={col.key}
                align={col.align || 'right'}
                onClick={() => handleSort(col.key as keyof PlatformDataItem)}
              >
                {col.label}
                {getSortIndicator(col.key as keyof PlatformDataItem)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedData.length > 0 ? (
            sortedData.map((item) => (
              <TableRow key={item.platform}>
                <TableCell className={styles.tableCell}>
                  <Box className={styles.platformCell}>
                    <PlatformIcon platform={item.platform} />
                    <Typography variant="body2" className={styles.platformName}>
                      <PlatformName platform={item.platform} />
                    </Typography>
                  </Box>
                </TableCell>
                {columns.map((col) => (
                  <TableCell
                    key={col.key}
                    align={col.align || 'right'}
                    className={styles.dataCell}
                  >
                    {loading ? (
                      <Skeleton width={30} />
                    ) : (
                      <Typography variant="body2" className={styles.value}>
                        {col.format ? col.format(item[col.key]) : item[col.key]}
                      </Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length + 1}>
                <div>-</div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  )
}
